import type { FC } from 'react'

import { InfoBox, StyledText } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import AlertIcon from 'mdi-react/AlertOutlineIcon'
import { useParams } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import queries from '@/modules/admin/queries'
import { leasingResources } from '@/resources/leasing'

import { daysBeforeEndOfLease } from './helper'

export const EndOfLeaseWarning: FC = () => {
  const params = useParams()
  const assetId = Number(params.id)
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()

  if (isNaN(assetId)) throw Error(translate('Asset ID is not a number!'))

  const { data, isLoading, isError } = useQuery(queries.adminLeasingAssetDetails(assetId), () =>
    leasingResources.getLeasingAssetDetails(assetId)
  )

  if (isLoading || isError || !data || !data?.leasingInfo?.leaseEnd) {
    return null
  }

  return (
    <InfoBox emphasis="warning" className="flex-row items-start justify-start">
      <StyledText colorMode="warning">
        <AlertIcon />
      </StyledText>

      <div className="flex flex-col gap-2">
        <StyledText colorMode="warning" size="regular-plus" fontWeight="bold">
          <Translation
            id="Action required - lease ends in {d} {d, plural, one {day} other {days}}."
            d={daysBeforeEndOfLease(data.leasingInfo.leaseEnd)}
          />
        </StyledText>
        <StyledText
          colorMode="warning"
          size="regular"
          fontWeight={whiteLabel === 'threeStepIT' || whiteLabel === 'telia' ? 'standard' : 'bold'}
        >
          <Translation
            id="The end-of-lease deadline is {date}. Select a next step or the company may face additional costs."
            date={data.leasingInfo.leaseEnd}
          />
        </StyledText>
      </div>
    </InfoBox>
  )
}
